import i18n from '@/i18n'

interface KnowledgeScopeFields {
  web?: boolean
  library?: boolean
  mode: 'all' | 'selected'
  documentTokens: Array<string>
  collectionTokens: Array<string>
}

/** Omitted / undefined channel flags are on (old rows and old client state). */
export function isScopeChannelOn(value: boolean | undefined): boolean {
  return value !== false
}

function hasLibraryItemFilter(scope: KnowledgeScopeFields): boolean {
  if (!isScopeChannelOn(scope.library)) {
    return false
  }
  if (scope.mode !== 'selected') {
    return false
  }
  return scope.documentTokens.length > 0 || scope.collectionTokens.length > 0
}

/** Any = web on + library on + no library item filter. */
export function isAnyScope(scope: KnowledgeScopeFields): boolean {
  return (
    isScopeChannelOn(scope.web) &&
    isScopeChannelOn(scope.library) &&
    !hasLibraryItemFilter(scope)
  )
}

function selectedItemCount(scope: KnowledgeScopeFields): number {
  return scope.documentTokens.length + scope.collectionTokens.length
}

export function scopeChipLabel(scope: KnowledgeScopeFields): string {
  if (isAnyScope(scope)) {
    return i18n.t('threads.scope.chipAny', { ns: 'app' })
  }
  if (hasLibraryItemFilter(scope)) {
    const n = selectedItemCount(scope)
    return i18n.t('threads.scope.itemsSelected', { ns: 'app', count: n })
  }
  if (isScopeChannelOn(scope.web) && !isScopeChannelOn(scope.library)) {
    return i18n.t('threads.scope.chipWeb', { ns: 'app' })
  }
  if (isScopeChannelOn(scope.library) && !isScopeChannelOn(scope.web)) {
    return i18n.t('threads.scope.chipLibrary', { ns: 'app' })
  }
  return i18n.t('threads.scope.chipWeb', { ns: 'app' })
}

export function threadScopeChipLabel(scope: KnowledgeScopeFields): string {
  if (isAnyScope(scope)) {
    return i18n.t('threads.scope.chipAny', { ns: 'app' })
  }
  if (hasLibraryItemFilter(scope)) {
    const n = selectedItemCount(scope)
    return i18n.t('threads.scope.items', { ns: 'app', count: n })
  }
  if (isScopeChannelOn(scope.web) && !isScopeChannelOn(scope.library)) {
    return i18n.t('threads.scope.chipWeb', { ns: 'app' })
  }
  if (isScopeChannelOn(scope.library) && !isScopeChannelOn(scope.web)) {
    return i18n.t('threads.scope.chipLibrary', { ns: 'app' })
  }
  return i18n.t('threads.scope.chipWeb', { ns: 'app' })
}

export function formatThreadRelativeTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  if (diffMinutes < 1) return i18n.t('threads.scope.justNow', { ns: 'app' })
  if (diffMinutes < 60) return i18n.t('threads.scope.minutesAgo', { ns: 'app', count: diffMinutes })

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return i18n.t('threads.scope.hoursAgo', { ns: 'app', count: diffHours })

  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const startOfYesterday = new Date(startOfToday)
  startOfYesterday.setDate(startOfYesterday.getDate() - 1)

  if (date >= startOfYesterday && date < startOfToday) {
    return i18n.t('threads.scope.yesterday', { ns: 'app' })
  }

  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

export function threadSourceCountLabel(n: number): string {
  if (n <= 0) return ''
  return i18n.t('threads.scope.source', { ns: 'app', count: n })
}
